import { useState, FormEvent, ChangeEvent } from 'react';
import ScrollToTop from './ScrollToTop';
import './css/AssessmentIntake.css';

const PAIN_AREAS = [
  { value: 'lead_response',        label: 'Lead Response & Speed-to-Lead' },
  { value: 'email',                label: 'Email Management & Inbox' },
  { value: 'meeting_notes',        label: 'Meeting Notes & Action Items' },
  { value: 'content_creation',     label: 'Content Creation & Marketing' },
  { value: 'scheduling',           label: 'Scheduling & Calendar Management' },
  { value: 'bookkeeping',          label: 'Bookkeeping & Invoicing' },
  { value: 'document_creation',    label: 'Document Creation & Proposals' },
  { value: 'customer_support',     label: 'Customer Support & FAQ' },
  { value: 'crm',                  label: 'CRM & Sales Pipeline' },
  { value: 'reporting',            label: 'Reporting & Analytics' },
  { value: 'project_management',   label: 'Project Management & Task Tracking' },
  { value: 'workflow_automation',  label: 'Workflow Automation & Integration' },
];

interface FormState {
  name: string;
  email: string;
  phone: string;
  business_name: string;
  business_type: string;
  website: string;
  team_size: string;
  hourly_rate: string;
  pain_areas_flagged: string[];
  tools_already_using: string;
  call_date: string;
  call_time: string;
}

const EMPTY: FormState = {
  name: '',
  email: '',
  phone: '',
  business_name: '',
  business_type: '',
  website: '',
  team_size: '1',
  hourly_rate: '85',
  pain_areas_flagged: [],
  tools_already_using: '',
  call_date: '',
  call_time: '',
};

function todayStr() {
  return new Date().toISOString().split('T')[0];
}

function minTimeForDate(date: string): string {
  if (date !== todayStr()) return '';
  const now = new Date();
  now.setMinutes(now.getMinutes() + 15);
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
}

export default function AssessmentIntake() {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ slug: string; call_scheduled_at: string } | null>(null);
  const [error, setError] = useState('');

  function handleChange(e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  function handleCheckbox(value: string) {
    const current = form.pain_areas_flagged;
    setForm({
      ...form,
      pain_areas_flagged: current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value],
    });
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    const payload = {
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      business_name: form.business_name.trim(),
      business_type: form.business_type.trim(),
      website: (() => {
        const raw = form.website.trim();
        if (!raw) return null;
        const match = raw.match(/^(https?)[:/\\]+/i);
        const protocol = match ? match[1].toLowerCase() : 'https';
        const stripped = raw.replace(/^https?:[\/\\]+/i, '');
        return `${protocol}://${stripped}`;
      })(),
      team_size: parseInt(form.team_size) || 1,
      hourly_rate: parseInt(form.hourly_rate) || 85,
      pain_areas_flagged: form.pain_areas_flagged,
      tools_already_using: form.tools_already_using
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
      call_scheduled_at: new Date(`${form.call_date}T${form.call_time}`).toISOString(),
    };

    try {
      const res = await fetch('/api/assessment-intake', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.ok) {
        setResult({ slug: data.client_slug, call_scheduled_at: data.call_scheduled_at });
        setForm(EMPTY);
      } else {
        setError(data.error || 'Something went wrong.');
      }
    } catch {
      setError('Could not reach the server. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (result) {
    const callTime = new Date(result.call_scheduled_at).toLocaleString(undefined, {
      weekday: 'long', month: 'long', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
    return (
      <div className="intake-page">
        <div className="intake-success">
          <div className="intake-success-icon">✓</div>
          <h2>You're all set!</h2>
          <p>Your assessment call is scheduled for <strong>{callTime}</strong>. You'll receive a call from our AI agent at that time.</p>
          <div className="intake-slug-block">
            <span className="intake-slug-label">Your reference ID</span>
            <code>{result.slug}</code>
          </div>
          <a className="btn btn-primary" href="/">
            Back to Home
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="intake-page">
      <div className="intake-container">
        <div className="intake-header">
          <h1>AI Business Assessment</h1>
          <p>Tell us a bit about your business before your assessment call. This helps us tailor the conversation to what matters most to you.</p>
        </div>

        <form className="intake-form" onSubmit={handleSubmit}>

          <div className="intake-section">
            <h3>About You</h3>

            <div className="form-group">
              <label htmlFor="name">Your Name *</label>
              <input
                type="text" id="name" name="name"
                value={form.name} onChange={handleChange}
                placeholder="Jane Smith" required disabled={submitting}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email" id="email" name="email"
                  value={form.email} onChange={handleChange}
                  placeholder="jane@smithrealty.com" required disabled={submitting}
                />
              </div>
              <div className="form-group">
                <label htmlFor="phone">Phone Number *</label>
                <input
                  type="tel" id="phone" name="phone"
                  value={form.phone} onChange={handleChange}
                  placeholder="+1 (555) 000-0000" required disabled={submitting}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="business_name">Business Name *</label>
              <input
                type="text" id="business_name" name="business_name"
                value={form.business_name} onChange={handleChange}
                placeholder="Smith Realty Group" required disabled={submitting}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="business_type">Business Type *</label>
                <input
                  type="text" id="business_type" name="business_type"
                  value={form.business_type} onChange={handleChange}
                  placeholder="Real estate agent, marketing agency..." required disabled={submitting}
                />
              </div>
              <div className="form-group form-group--short">
                <label htmlFor="team_size">Team Size</label>
                <input
                  type="number" id="team_size" name="team_size"
                  value={form.team_size} onChange={handleChange}
                  min="1" max="500" disabled={submitting}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="website">Business Website</label>
              <input
                type="text" id="website" name="website"
                value={form.website} onChange={handleChange}
                placeholder="yoursite.com"
                disabled={submitting}
              />
              <span className="form-hint">Optional — we'll review your site as part of the assessment.</span>
            </div>

            <div className="form-group">
              <label htmlFor="hourly_rate">Your Hourly Value ($)</label>
              <input
                type="number" id="hourly_rate" name="hourly_rate"
                value={form.hourly_rate} onChange={handleChange}
                min="1" placeholder="85" disabled={submitting}
              />
              <span className="form-hint">Best guess at what your time is worth per hour. Used to calculate your ROI.</span>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="call_date">Preferred Call Date *</label>
                <input
                  type="date" id="call_date" name="call_date"
                  value={form.call_date} onChange={handleChange}
                  min={todayStr()} required disabled={submitting}
                />
              </div>
              <div className="form-group">
                <label htmlFor="call_time">Preferred Call Time *</label>
                <input
                  type="time" id="call_time" name="call_time"
                  value={form.call_time} onChange={handleChange}
                  min={minTimeForDate(form.call_date)}
                  required disabled={submitting}
                />
                <span className="form-hint">Times are in your local timezone.</span>
              </div>
            </div>
          </div>

          <div className="intake-section">
            <h3>Where Are You Losing Time?</h3>
            <p className="intake-section-desc">Check everything that feels painful or inefficient. No wrong answers.</p>
            <div className="pain-areas-grid">
              {PAIN_AREAS.map(({ value, label }) => (
                <label key={value} className={`pain-area-item ${form.pain_areas_flagged.includes(value) ? 'checked' : ''}`}>
                  <input
                    type="checkbox"
                    checked={form.pain_areas_flagged.includes(value)}
                    onChange={() => handleCheckbox(value)}
                    disabled={submitting}
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="intake-section">
            <h3>Tools You Already Use</h3>
            <div className="form-group">
              <label htmlFor="tools_already_using">Current Tools (comma-separated)</label>
              <input
                type="text" id="tools_already_using" name="tools_already_using"
                value={form.tools_already_using} onChange={handleChange}
                placeholder="Gmail, Google Calendar, QuickBooks, HubSpot..."
                disabled={submitting}
              />
              <span className="form-hint">We won't recommend tools you already have.</span>
            </div>
          </div>

          {error && <div className="form-message error">{error}</div>}

          <button type="submit" className="btn btn-primary intake-submit" disabled={submitting}>
            {submitting ? 'Scheduling...' : 'Schedule My Assessment Call'}
          </button>
        </form>
      </div>
      <ScrollToTop />
    </div>
  );
}
